/**
 * Serviço de Rate Limiting no Frontend
 * Evita muitas requisições simultâneas que causam HTTP 429
 */

class RateLimiterService {
  constructor() {
    // Configurações
    this.maxRequestsPerSecond = 2; // Máximo 2 requisições por segundo
    this.maxConcurrentRequests = 3; // Máximo 3 requisições simultâneas
    this.retryDelay = 1000; // 1 segundo de delay entre retries
    
    // Estado
    this.requestQueue = [];
    this.activeRequests = 0;
    this.lastRequestTime = 0;
    this.retryAttempts = new Map();
    
    // Iniciar processamento da fila
    this.startQueueProcessor();
  }

  /**
   * Executar requisição com rate limiting
   * @param {Function} requestFn - Função que executa a requisição
   * @param {string} url - URL da requisição
   * @param {Object} params - Parâmetros da requisição
   * @param {string} method - Método HTTP
   * @param {number} timeout - Timeout em ms
   * @returns {Promise} - Promise da requisição
   */
  async executeRequest(requestFn, url, params = {}, method = 'GET', timeout = 30000) {
    return new Promise((resolve, reject) => {
      const requestId = this.generateRequestId();
      
      this.requestQueue.push({
        id: requestId,
        requestFn,
        url,
        params,
        method,
        timeout,
        resolve,
        reject,
        timestamp: Date.now()
      });
      
    });
  }

  /**
   * Processar fila de requisições
   */
  startQueueProcessor() {
    setInterval(() => {
      this.processQueue();
    }, 100); // Verificar a cada 100ms
  }

  /**
   * Processar fila de requisições
   */
  async processQueue() {
    // Verificar se podemos processar mais requisições
    if (this.activeRequests >= this.maxConcurrentRequests) {
      return;
    }

    // Verificar rate limiting por segundo
    const now = Date.now();
    if (now - this.lastRequestTime < (1000 / this.maxRequestsPerSecond)) {
      return;
    }

    // Pegar próxima requisição da fila
    const request = this.requestQueue.shift();
    if (!request) {
      return;
    }

    this.activeRequests++;
    this.lastRequestTime = now;


    try {
      const result = await this.executeRequestInternal(request);
      request.resolve(result);
    } catch (error) {
      // Verificar se é erro 429 (rate limiting)
      if (error.response?.status === 429) {
        console.warn(`⏰ Rate limit atingido para ${request.url}, aguardando...`);
        
        // Adicionar delay e tentar novamente
        setTimeout(() => {
          this.requestQueue.unshift(request); // Voltar para o início da fila
        }, this.retryDelay);
        
        this.activeRequests--;
        return;
      }
      
      request.reject(error);
    } finally {
      this.activeRequests--;
    }
  }

  /**
   * Executar requisição interna
   */
  async executeRequestInternal(request) {
    const { requestFn, params, timeout } = request;
    
    // Executar requisição com timeout
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Request timeout')), timeout)
    );
    
    const requestPromise = requestFn(params);
    
    return Promise.race([requestPromise, timeoutPromise]);
  }

  /**
   * Gerar ID único para requisição
   */
  generateRequestId() {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Verificar se há requisições pendentes
   */
  hasPendingRequests() {
    return this.requestQueue.length > 0 || this.activeRequests > 0;
  }

  /**
   * Limpar fila de requisições
   */
  clearQueue() {
    this.requestQueue.forEach(request => {
      request.reject(new Error('Queue cleared'));
    });
    this.requestQueue = [];
    this.activeRequests = 0;
  }

  /**
   * Obter estatísticas da fila
   */
  getStats() {
    return {
      queueLength: this.requestQueue.length,
      activeRequests: this.activeRequests,
      maxConcurrent: this.maxConcurrentRequests,
      maxPerSecond: this.maxRequestsPerSecond
    };
  }
}

// Instância singleton
const rateLimiterService = new RateLimiterService();

export default rateLimiterService;
